import { CSSProperties } from 'react'

import { BrickColor } from '../../Brick/Brick'
import { StudRange } from '../Baseplate'

/**
 * Calculates the start and end positions for the stud range based on the snap position.
 */
export const calculateStudRangePositions = (
  rect: DOMRect,
  baseplateRect: DOMRect,
  range: StudRange,
  gridUnit: number
): {
  startRow: number
  endRow: number
  startColumn: number
  endColumn: number
  offsetColumn: number
} => {
  let startRow, endRow, startColumn, endColumn
  const offsetRow = (range.offset?.bottom || 0) - (range.offset?.top || 0)
  const offsetColumn = (range.offset?.right || 0) - (range.offset?.left || 0)
  // Calculate start and end positions based on the snap position
  switch (range.snapPosition) {
    case 'top-left':
      // Calculate the starting row and column based on the top-left corner of the element
      startRow = Math.floor((rect.top - baseplateRect.top + offsetRow * gridUnit) / gridUnit)

      startColumn = Math.floor(
        (rect.left - baseplateRect.left + offsetColumn * gridUnit) / gridUnit
      )
      // Calculate the ending row and column, either covering the full height/width or based on the specified size
      endRow =
        range.size.rows === -1
          ? Math.floor(baseplateRect.height / gridUnit) - 1
          : startRow + range.size.rows - 1
      endColumn =
        range.size.columns === -1
          ? Math.floor(baseplateRect.width / gridUnit) - 1
          : startColumn + range.size.columns - 1
      break

    case 'top-right':
      // Calculate the starting row based on the top-right corner of the element
      startRow = Math.floor((rect.top - baseplateRect.top + offsetRow * gridUnit) / gridUnit)
      // Calculate the ending column based on the right edge of the element
      endColumn =
        range.size.columns === -1
          ? Math.floor(baseplateRect.width / gridUnit) - 1
          : Math.ceil(
              (rect.left + rect.width - baseplateRect.left + offsetColumn * gridUnit) / gridUnit
            ) - 1
      // Calculate the starting column based on the ending column and the size
      startColumn = endColumn - range.size.columns + 1
      // Calculate the ending row, either covering the full height or based on the specified size
      endRow =
        range.size.rows === -1
          ? Math.floor(baseplateRect.height / gridUnit) - 1
          : startRow + range.size.rows - 1
      break

    case 'bottom-left':
      // Calculate the ending row based on the bottom-left corner of the element
      endRow =
        range.size.rows === -1
          ? Math.floor(baseplateRect.height / gridUnit) - 1
          : Math.ceil(
              (rect.top + rect.height - baseplateRect.top + offsetRow * gridUnit) / gridUnit
            ) - 1
      // Calculate the starting row based on the ending row and the size
      startRow = endRow - range.size.rows + 1
      // Calculate the starting column based on the left edge of the element
      startColumn = Math.floor(
        (rect.left - baseplateRect.left + offsetColumn * gridUnit) / gridUnit
      )
      // Calculate the ending column, either covering the full width or based on the specified size
      endColumn =
        range.size.columns === -1
          ? Math.floor(baseplateRect.width / gridUnit) - 1
          : startColumn + range.size.columns - 1
      break

    case 'bottom-right':
      // Calculate the ending row based on the bottom-right corner of the element
      endRow =
        range.size.rows === -1
          ? Math.floor(baseplateRect.height / gridUnit) - 1
          : Math.ceil(
              (rect.top + rect.height - baseplateRect.top + offsetRow * gridUnit) / gridUnit
            ) - 1
      // Calculate the ending column based on the right edge of the element
      endColumn =
        range.size.columns === -1
          ? Math.floor(baseplateRect.width / gridUnit) - 1
          : Math.ceil((rect.left + rect.width - baseplateRect.left) / gridUnit) - 1
      // Calculate the starting row based on the ending row and the size
      startRow = endRow - range.size.rows + 1
      // Calculate the starting column based on the ending column and the size
      startColumn = endColumn - range.size.columns + 1
      break

    default:
      return { startRow: -1, endRow: -1, startColumn: -1, endColumn: -1, offsetColumn }
  }

  return { startRow, endRow, startColumn, endColumn, offsetColumn }
}

/**
 * Calculates the positions of the bricks based on the stud range and snap position.
 */
export const calculateBrickPositions = (
  studRanges: StudRange[],
  baseplateRect: DOMRect,
  gridUnit: number
) => {
  const bricks: { style: CSSProperties; color: BrickColor }[] = []

  studRanges.forEach((range) => {
    let startRow: number, startColumn: number

    switch (range.snapPosition) {
      case 'top-left':
        startRow = 0
        startColumn = 0
        break
      case 'top-right':
        startRow = 0
        startColumn = Math.floor(baseplateRect.width / gridUnit) - range.size.columns
        break
      case 'bottom-left':
        startRow = Math.floor(baseplateRect.height / gridUnit) - range.size.rows
        startColumn = 0
        break
      case 'bottom-right':
        startRow = Math.floor(baseplateRect.height / gridUnit) - range.size.rows
        startColumn = Math.floor(baseplateRect.width / gridUnit) - range.size.columns
        break
      default:
        return
    }

    if (range.bricks) {
      range.bricks.forEach((brick) => {
        let top = (startRow + brick.row - 1) * gridUnit
        let left: number | undefined = (startColumn + brick.column - 1) * gridUnit
        let right: number | undefined = undefined

        switch (brick.snapPosition) {
          case 'top-left':
            break // No adjustment needed
          case 'top-right':
            left = undefined
            right = baseplateRect.width - (startColumn + brick.column) * gridUnit
            break
          case 'bottom-left':
            top = top - gridUnit
            break
          case 'bottom-right':
            top = top - gridUnit
            left = undefined
            right = baseplateRect.width - (startColumn + brick.column) * gridUnit
            break
        }

        top = Math.floor(top / gridUnit) * gridUnit
        if (left !== undefined) {
          left = Math.floor(left / gridUnit) * gridUnit
        }
        if (right !== undefined) {
          right = Math.floor(right / gridUnit) * gridUnit
        }

        const brickStyle: CSSProperties = {
          top,
          left,
          right,
          position: 'absolute',
          width: 2 * gridUnit,
          height: 2 * gridUnit,
        }
        bricks.push({ style: brickStyle, color: brick.color })
      })
    }
  })

  return bricks
}
